import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { incentiveSettingsApi, getErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { Sparkles, Loader2, Save, Pencil } from 'lucide-react';

/**
 * Small admin-only inline editor for the org-wide incentive rate (a single
 * percentage of joined CTC). Sits at the top of the Reports → Recruiter
 * Revenue tab so admins can adjust the dial without leaving the page.
 *
 * Persists via PUT /api/settings/incentive. The Recruiter Revenue heat-map
 * picks up the new rate on its next reload (the parent bumps a `reloadKey`
 * which we don't own here — keep this component dumb and just call onSaved).
 */
const IncentiveRateEditor = ({ onSaved }) => {
  const [rate, setRate] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    incentiveSettingsApi
      .get()
      .then((r) => {
        const pct = Number(r.data?.incentive_pct_of_ctc || 0);
        setRate(pct);
        setDraft(String(pct));
        setCanEdit(Boolean(r.data?.can_edit));
      })
      .catch(() => setRate(0))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const n = Number(draft);
    if (Number.isNaN(n) || n < 0 || n > 100) {
      toast.error('Rate must be between 0 and 100');
      return;
    }
    setSaving(true);
    try {
      const r = await incentiveSettingsApi.put(n);
      const next = Number(r.data?.incentive_pct_of_ctc || 0);
      setRate(next);
      setEditing(false);
      toast.success(next > 0
        ? `Incentive set to ${next}% of joined CTC`
        : 'Incentive disabled');
      if (typeof onSaved === 'function') onSaved(next);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save incentive rate'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading incentive…
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-center gap-3 flex-wrap"
      data-testid="incentive-rate-editor"
    >
      <div className="flex items-center gap-1.5 text-xs text-emerald-900 font-semibold">
        <Sparkles className="w-3.5 h-3.5" />
        Incentive rate
      </div>
      {!editing ? (
        <>
          <span
            className="font-mono text-sm font-bold text-emerald-800"
            data-testid="incentive-rate-display"
          >
            {rate > 0 ? `${rate}%` : 'Off'}
          </span>
          <span className="text-[11px] text-emerald-700/80">
            {rate > 0
              ? 'of joined CTC · row totals show earned ₹ pill'
              : canEdit
              ? 'set a % to compute "earned" pill per recruiter'
              : 'incentive not configured yet'}
          </span>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 ml-auto"
              onClick={() => {
                setDraft(String(rate ?? 0));
                setEditing(true);
              }}
              data-testid="incentive-rate-edit-btn"
            >
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
          )}
        </>
      ) : (
        <div className="flex items-center gap-2 ml-auto">
          <Label htmlFor="incentive-rate-input" className="sr-only">
            Incentive percent
          </Label>
          <Input
            id="incentive-rate-input"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-24 text-sm bg-white"
            data-testid="incentive-rate-input"
            placeholder="e.g. 1"
          />
          <span className="text-[11px] text-emerald-800/80">% of joined CTC</span>
          <Button
            size="sm"
            className="h-7 px-2 bg-emerald-700 hover:bg-emerald-800"
            onClick={save}
            disabled={saving}
            data-testid="incentive-rate-save-btn"
          >
            {saving ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Save className="w-3 h-3 mr-1" /> Save
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={() => {
              setEditing(false);
              setDraft(String(rate ?? 0));
            }}
            disabled={saving}
            data-testid="incentive-rate-cancel-btn"
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default IncentiveRateEditor;
